import { useCallback, useState } from 'react';

import { useTracking } from '@change-corgi/core-react/tracking';

import type { PostTypeDataMap } from '#src/app/pages/starterDashboardV2/shared/types';
import type { EnrichedReadyToSharePlatformPostsV2 } from '#src/app/shared/api/readyToShare';

import { useHandlePolling } from '../useHandlePolling';

type UseReadyToShareResult = ModelHookResult<
	{
		isOpen: boolean;
		shouldShowReadyToShareExperiment: boolean;
		isLoading: boolean;
		readyToSharePostsValue?: EnrichedReadyToSharePlatformPostsV2;
		postTypeData: PostTypeDataMap;
	},
	{
		openSharePreviewModal: () => void;
		closeSharePreviewModal: () => void;
		trackView: (currentPosition: string) => void;
	}
>;

export const PRE_MADE_POST_MODULE_VIEW_EVENT_NAME = 'petition_manage_ready_to_share_posts_view_v2';
export const SOCIAL_CONTENT_VIEW_EVENT_NAME = 'petition_manage_social_content_view';
export const PRE_MADE_POST_MODULE_CLICK_EVENT_NAME = 'petition_manage_ready_to_share_posts_click_v2';
export const PRE_MADE_POST_MODULE_EVENT_COMMON_PROPERTIES = {
	context: 'petition_dashboard_ready_to_share_post',
	view_context: 'petition_dashboard_ready_to_share_post',
};

export function useReadyToShare(): UseReadyToShareResult {
	const [isOpen, setOpen] = useState(false);
	const [isTracked, setIsTracked] = useState(false);
	const track = useTracking();
	const data = useHandlePolling();

	const trackView = useCallback(
		(currentPosition: string) => {
			if (isTracked || currentPosition === 'below') {
				return;
			}
			void track(PRE_MADE_POST_MODULE_VIEW_EVENT_NAME, PRE_MADE_POST_MODULE_EVENT_COMMON_PROPERTIES);
			void track(SOCIAL_CONTENT_VIEW_EVENT_NAME, PRE_MADE_POST_MODULE_EVENT_COMMON_PROPERTIES);
			setIsTracked(true);
		},
		[track, setIsTracked, isTracked],
	);

	const openSharePreviewModal = useCallback(() => {
		setOpen(true);
		void track(PRE_MADE_POST_MODULE_CLICK_EVENT_NAME, PRE_MADE_POST_MODULE_EVENT_COMMON_PROPERTIES);
	}, [setOpen, track]);

	const closeSharePreviewModal = useCallback(() => {
		setOpen(false);
	}, [setOpen]);

	return {
		data: {
			isOpen,
			...data,
		},
		actions: {
			openSharePreviewModal,
			closeSharePreviewModal,
			trackView,
		},
	};
}
